import React, {useEffect} from 'react';
import {IonButton, IonContent, IonHeader, IonButtons, IonBackButton, IonPage, IonIcon, IonToolbar, IonGrid, IonRow, IonCol, IonCardTitle, IonLabel, IonInput, IonCard, IonCardHeader, IonCardContent, IonLoading} from '@ionic/react';
import {arrowBack, shapesOutline} from "ionicons/icons";
import {useMutation, useQuery} from "@apollo/client";
import {COMPLETE_PASSWORD_RESET, VERIFY_PASSWORD_RESET_TOKEN} from "../schemas/resetpassword";
import {useRouteMatch} from "react-router";
import jwt_decode from "jwt-decode";
import {useHistory} from "react-router-dom";
import useNotificationModal from "../hooks/useNotificationModal";

const ResetPassword: React.FC<any> = () => {
    const match: any = useRouteMatch();
    const history = useHistory();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [email, setEmail] = React.useState<string>('');
    const [password, setPassword] = React.useState<string>('');
    const [confirmPassword, setConfirmPassword] = React.useState<string>('');
    const {loading: verifyLoading, error: verifyError, data: verifyData} = useQuery(VERIFY_PASSWORD_RESET_TOKEN, {variables: {resetToken: match.params.token}});
    const [completePasswordResetMutation] = useMutation(COMPLETE_PASSWORD_RESET);
    const {showSuccessModal, showErrorModal} = useNotificationModal();

    useEffect(() => {
        try {
            const token = match.params.token;
            const resetParams: { email: string, userId: number, generatedAt: string } = jwt_decode(token);
            setEmail(resetParams.email);
        } catch (e: any) {
            console.error(e);
            showErrorModal((e?.message ?? 'An unknown error occurred'));
            history.push('/');
        }
    }, [history, match.params.token]);

    useEffect(() => {
        if (!verifyLoading) {
            if (!verifyData?.verifyPasswordResetToken?.success) {
                showErrorModal((verifyData?.verifyPasswordResetToken?.error ?? verifyError ?? 'An unknown error occurred'));
                history.push('/');
            }
        }
    }, [history, verifyData, verifyError, verifyLoading]);

    const updatePassword = async () => {
        setIsLoading(true);
        try {
            const resetPasswordResults = (await completePasswordResetMutation({
                variables: {
                    email,
                    password,
                    confirmPassword,
                    resetToken: match.params.token,
                }
            })).data?.completePasswordReset;
            if (!resetPasswordResults?.success) {
                showErrorModal((resetPasswordResults?.error ?? 'An unknown error occurred'));
            } else {
                showSuccessModal('Your password has been successfully updated.');
                history.push('/');
            }
        } catch (e: any) {
            console.error(e);
            showErrorModal((e?.message ?? 'An unknown error occurred'));
        } finally {
            setIsLoading(false);
        }
    }

    return (<>
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonBackButton icon={arrowBack} text="" className="custom-back"/>
                        </IonButtons>

                        <IonButtons slot="end">
                            <IonButton className="custom-button">
                                <IonIcon icon={shapesOutline}/>
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent fullscreen>
                    <IonLoading isOpen={(verifyLoading || isLoading)} message={'Loading...'}/>
                    <IonGrid className="ion-padding">
                        <IonRow className="ion-justify-content-center">
                            <IonCol sizeLg="4">
                                <IonCard>
                                    <IonCardHeader>
                                        <IonCardTitle>Password Reset</IonCardTitle>
                                        <h5>Please input your new password</h5>
                                    </IonCardHeader>
                                    <IonCardContent>
                                        <div>
                                            <IonLabel>Username</IonLabel>
                                            <IonInput disabled value={email} required type='email' placeholder={'Username'}/>
                                        </div>
                                        <div>
                                            <IonLabel>New Password</IonLabel>
                                            <IonInput onIonChange={(e) => setPassword((e?.detail?.value ?? ''))} required type='password'
                                                      autocomplete={'new-password'} placeholder={'New Password'}/>
                                        </div>
                                        <div>
                                            <IonLabel>Confirm Password</IonLabel>
                                            <IonInput onIonChange={(e) => setConfirmPassword((e?.detail?.value ?? ''))} required
                                                      type='password'
                                                      autocomplete={'new-password'} placeholder={'Confirm Password'}/>
                                        </div>
                                        <IonButton disabled={!email || !password || !confirmPassword} className="custom-button"
                                                   expand="block"
                                                   onClick={() => updatePassword()}>Update Password</IonButton>
                                    </IonCardContent>
                                    <IonCardContent style={{display: 'flex', justifyContent: 'flex-end'}}>
                                        <IonButton size={"small"} fill={"clear"} color={"secondary"} routerLink={'/'}>
                                            Back to Login
                                        </IonButton>
                                    </IonCardContent>
                                </IonCard>
                            </IonCol>
                        </IonRow>
                    </IonGrid>
                </IonContent>
            </IonPage>
        </>
    );
};

export default ResetPassword;
